import React, { useEffect, useState, useRef } from 'react';
import { GET_GOOGLEDRIVE_TOKEN } from '~/services/integration';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';

const GoogleDriveIntegration: React.FC = () => {
  const [authCode, setAuthCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [getGoogleDriveToken, { loading }] = useMutation(GET_GOOGLEDRIVE_TOKEN);
  const [success, setSuccess] = useState<boolean | null>(null);
  const effectRan = useRef(false); // Ref to track if the effect has already run
  const queryParams = new URLSearchParams(window.location.search);
  let agentId  = queryParams.get('state') || '';
  

  useEffect(() => {
    // console.log('GoogleDriveIntegration effect running. effectRan.current:', effectRan.current);
    // Only run the fetch logic if it hasn't run before (to handle React.StrictMode double invocation in development)
    if (effectRan.current === false) {
      const fetchToken = async () => {
        // Solo ejecuta en el cliente
        if (typeof window === 'undefined') {
          // console.log('Not in browser environment, skipping fetchToken.');
          return;
        }
        // console.log('Calling fetchToken...');
        const queryParams = new URLSearchParams(window.location.search);
        const code = queryParams.get('code');

        if (!code) {
          setError('No se encontró el parámetro "code" en la URL.');
          return;
        }

        setAuthCode(code);
        try {
          // console.log('Attempting to get Google Drive token with code:', code);
          const res = await getGoogleDriveToken({
            variables: {
              data: {
                token: code,
                agentId
              },
            },
          });
          const responseData = res.data?.get_googleDrive_token;
          // console.log('Response from Google Drive token mutation:', responseData);
          if (responseData?.hasError) {
            setError('El token de Google Drive no es válido.');
            setSuccess(false);
          } else {
            setSuccess(true);
          }
        } catch (e: any) {
          // console.error('Error fetching Google Drive token:', e);
          setError('Error al obtener el token de Google Drive.');
          setSuccess(false);
        }
      };
      fetchToken();
    }

    // Cleanup function: will run on unmount or before the effect runs again.
    // In StrictMode, this runs after the first mount, setting effectRan.current to true.
    // So, on the second mount (in StrictMode), the condition effectRan.current === false will be false.
    return () => {
      // console.log('GoogleDriveIntegration effect cleanup. Setting effectRan.current = true.');
      effectRan.current = true;
    };
  }, []); // Empty dependency array ensures this runs once after initial mount (plus StrictMode behavior)

  // Helper function to handle redirects
  const redirectToIntegrations = (status: 'success' | 'error') => {
    const currentPathname = window.location.pathname;
    const integrationsPath = currentPathname.substring(0, currentPathname.lastIndexOf('/google_drive'));

    const targetUrl = new URL(integrationsPath, `${window.location.origin}/agent/${agentId}/integrations`);
    targetUrl.searchParams.set('status', status);
    targetUrl.searchParams.set('service', 'googleDrive');
    console.log('Redirecting to:', targetUrl.href);
    window.location.replace(targetUrl.href);
  };

  if (error) {
     const currentPathname = window.location.pathname;
    const integrationsPath = currentPathname.substring(0, currentPathname.lastIndexOf('/google_drive'));

    const targetUrl = new URL(integrationsPath, `${window.location.origin}/agent/${agentId}/integrations`);
    targetUrl.searchParams.set('status', "error");
    targetUrl.searchParams.set('service', 'googleDrive');
    console.log('Redirecting to:', targetUrl.href);
    window.location.replace(targetUrl.href);
  }

  if (loading) {
    return (
      <div>
        <h1>Integración con Google Drive</h1>
        <p>Cargando...</p>
      </div>
    );
  }

  if (!authCode) {
    return (
      <div>
        <h1>Integración con Google Drive</h1>
        <p>Procesando la autenticación...</p>
      </div>
    );
  }

  if (success === false) {
    // Redirect on failed authentication instead of showing error message
    redirectToIntegrations('error');
    return null;
  }

  if (success === true) {
    // Reuse the same redirect logic for success case
    redirectToIntegrations('success');
    return null;
  }

  return (
    <div>
      <h1>¡Integración con Google Drive Exitosa!</h1>
      <p>
        Este código se utilizará para completar el proceso de autenticación
        y acceder a tus archivos de Google Drive según los permisos que otorgaste.
      </p>
    </div>
  );
};

export default GoogleDriveIntegration;
